import { $consentAtom } from '@client/core/atoms/consent.js';
import * as RelevantDigital from '@client/core/relevantDigital/index.js';
import { sendErrorMessageMetricToServer } from '@client/core/services/metrics.js';
import { startConsentListener } from '@client/core/utils/consentListener.js';
import {
  getInitialState,
  hasInitialPlacements
} from '@client/core/utils/dom/getInitialState.js';
import {
  ConsentStatus,
  debug,
  type InitialState
} from '@schibsted-nmp/advertising-shared';

import { loadAndActivateI18n } from '../i18n.js';
import { setInitialGlobalAtoms } from './core/atoms/index.js';
import { addLifecycleEvent } from './core/atoms/metrics.js';
import { waitForConsent } from './core/utils/consent.js';
import {
  attachHighImpactRenderHandler,
  cleanupSeenThisHighImpactListener
} from './core/utils/SeenThis/SeenThis.js';
import { initiateAdvt } from './initiate.js';
import { Scripts } from './Scripts.js';
import { TcfApiMock } from './TcfApiMock.js';
import { TcfHelperMock } from './TcfHelperMock.js';

(async () => {
  try {
    const initialState = getInitialState();
    if (!initialState) return;

    if (initialState.env === 'local') {
      TcfApiMock.Window.inject();
      TcfHelperMock.Window.inject();
    }

    const consent = await waitForConsent();

    setupGlobals(initialState, consent);

    startConsentListener((nextConsent) => {
      try {
        $consentAtom.set(nextConsent);
      } catch (error) {
        debug.message('Failed to update consent atom', error).error();
      }
    });

    logForLocalEnvironment(initialState);

    if (!hasInitialPlacements(initialState)) return;

    loadAndActivateI18n(initialState.locale);

    try {
      await Promise.all(Scripts.load(initialState, consent));
      await initiateAdvt(initialState);

      attachHighImpactRenderHandler();

      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () => {
          cleanupSeenThisHighImpactListener();
        });
      }
    } catch (error) {
      debug.message('Failed initialization!', error).error();
    }
  } catch (error) {
    debug.message('Failed loading advertising-podlet!', error).error();

    if (error instanceof Error) {
      await sendErrorMessageMetricToServer(error.message);
    }
  }
})().catch((error: unknown) => {
  debug
    .message(
      'sendErrorMessageMetricToServer failed for advertising-podlet',
      error
    )
    .error();
});

function logForLocalEnvironment(initialState: InitialState) {
  const { config, env } = initialState;
  if (env !== 'local') return;

  if (!config.placements.length) {
    addLifecycleEvent('NB! No advertising placements returned from server');
    debug.message('NB! No advertising placements returned from server').warn();
  }
}

/**
 * Sets up some globals to manage ad config and related utilities.
 */
function setupGlobals(
  initialState: InitialState,
  consent: ConsentStatus
): void {
  RelevantDigital.GlobalJs.Window.inject();

  window.apntag ??= {} as NonNullable<Window['apntag']>;
  window.apntag['anq'] ??= [];
  setInitialGlobalAtoms(initialState, consent);
}
